import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { format, addDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaSort,
  FaSortDown,
  FaSortUp,
} from 'react-icons/fa';
import {
  useTotalBasesPredictions,
  useHitPredictions,
  useTwoHitsPredictions,
  useHrPredictions,
  useSportsbooks,
} from '../hooks/useApiQueries';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';
import { parseModelPageDateFromSearchParams, useDateQueryParam } from '../hooks/useDateQueryParam';
import TeamWithLogo from '../components/TeamWithLogo';
import PlayerWithAvatar from '../components/PlayerWithAvatar';
import SportsbookLogo from '../components/SportsbookLogo';
import SportsbookDropdown from '../components/Predictions/SportsbookDropdown.js';
import StatusWithGamedayLink from '../components/Predictions/StatusWithGamedayLink';
import MatchupCell from '../components/Predictions/MatchupCell';
import { formatPickTypeLabel } from '../utils/formatPickType';
import { buildModelPageDeepLink } from '../utils/modelPathForPickType';
import {
  filterPredictionsByTeam,
  formatTotalBasesPredDisplay,
  mergePlayerRows,
  passesLineupFilter,
  passesMinEdgeFilter,
  passesMinOddsFilter,
  sortTeamRows,
  hitPredValue,
  twoHitPredValue,
  hrPredValue,
} from '../utils/teamPicksMerge';
import { MLB_TEAMS, resolveMlbTeamDisplayName } from '../data/mlbTeams';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.25rem;
  @media (max-width: 600px) {
    padding: 0.75rem 0.6rem 1.25rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.35rem;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${(props) => props.theme.secondaryText};
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
`;

const DateNavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: ${(props) => props.theme.inputBg};
  border: 1.5px solid ${(props) => props.theme.primary};
  border-radius: 0.4rem;
  color: ${(props) => props.theme.primaryText};
  cursor: pointer;
  flex-shrink: 0;
  &:hover,
  &:focus {
    border-color: ${(props) => props.theme.primaryHover};
    outline: none;
  }
`;

const DateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => props.theme.inputBg};
  border: 1.5px solid ${(props) => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${(props) => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${(props) => props.theme.shadow};
`;

const CalendarIcon = styled(FaCalendarAlt)`
  color: ${(props) => props.theme.primary};
`;

const ChevronDownIcon = styled(FaChevronDown)`
  color: ${(props) => props.theme.mutedText};
`;

const DateButtonInput = React.forwardRef(({ value, onClick }, ref) => {
  const { theme } = useTheme();
  return (
    <DateButton type="button" onClick={onClick} ref={ref} theme={theme} aria-label={`Select date. Current: ${value}`}>
      <CalendarIcon theme={theme} />
      {value}
      <ChevronDownIcon theme={theme} />
    </DateButton>
  );
});

const FilterPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: ${(props) => props.theme.secondaryBg || 'rgba(255,255,255,0.04)'};
  border-radius: 0.5rem;
  border: 1px solid ${(props) => props.theme.border};
`;

const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => props.theme.mutedText};
  min-width: 0;
`;

const FilterSelect = styled.select`
  padding: 0.35rem 0.5rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  font-size: 0.9rem;
  max-width: 100%;
`;

const FilterInput = styled.input`
  padding: 0.35rem 0.5rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  font-size: 0.9rem;
  width: 6rem;
  max-width: 100%;
`;

const MobileSortSelect = styled(FilterSelect)`
  display: none;
  @media (max-width: 600px) {
    display: block;
    width: 100%;
    max-width: 320px;
  }
`;

const MobileOnlyFilterField = styled(FilterField)`
  @media (min-width: 601px) {
    display: none;
  }
`;

const TeamPickerBar = styled.div`
  width: 100%;
  max-width: 28rem;
  margin: 0 auto 1rem;
  padding: 0 0.25rem;
`;

const TeamSelect = styled.select`
  width: 100%;
  padding: 0.5rem 0.65rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  font-size: 0.95rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  font-size: 0.85rem;
  background: ${(props) => props.theme.cardBg};
  box-shadow: ${(props) => props.theme.shadow};
  border-radius: 0.5rem;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 0.45rem 0.4rem;
  text-align: left;
  font-weight: 600;
  background: ${(props) => props.theme.primary};
  color: ${(props) => props.theme.inverseText};
  font-size: 0.78rem;
  white-space: nowrap;
  cursor: ${(props) => (props.$sortable ? 'pointer' : 'default')};
`;

const ThGroup = styled.th`
  padding: 0.35rem 0.4rem;
  text-align: center;
  font-weight: 700;
  background: ${(props) => props.theme.tertiaryBg || props.theme.secondaryBg};
  color: ${(props) => props.theme.primaryText};
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const Td = styled.td`
  padding: 0.4rem 0.35rem;
  border-bottom: 1px solid ${(props) => props.theme.border};
  color: ${(props) => props.theme.primaryText};
  vertical-align: top;
  font-size: 0.82rem;
`;

const Tbody = styled.tbody`
  tr:nth-child(odd) {
    background: ${(props) => props.theme.tableRowBg};
  }
  tr:nth-child(even) {
    background: ${(props) => props.theme.tableRowHoverBg};
  }
  tr:hover {
    background: ${(props) => props.theme.tertiaryBg};
  }
`;

const DesktopTableSection = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
`;

const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 0.65rem;
  @media (max-width: 600px) {
    display: flex;
  }
`;

const PlayerCard = styled.div`
  background: ${(props) => props.theme.cardBg};
  border-radius: 0.5rem;
  box-shadow: ${(props) => props.theme.shadow};
  padding: 0.75rem 0.65rem;
  border-left: 3px solid ${(props) => props.theme.primary};
`;

const CardPlayerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ModelBlock = styled.div`
  margin-top: 0.45rem;
  padding-top: 0.45rem;
  border-top: 1px solid ${(props) => props.theme.border};
  &:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }
`;

const ModelBlockTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(props) => props.theme.primary};
  margin-bottom: 0.25rem;
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem 0.5rem;
  font-size: 0.82rem;
  color: ${(props) => props.theme.secondaryText};
`;

const Muted = styled.span`
  color: ${(props) => props.theme.secondaryText};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${(props) => props.theme.secondaryText};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background: ${(props) => props.theme.secondaryBg};
  border-radius: 0.5rem;
  border: 1px dashed ${(props) => props.theme.border};
  color: ${(props) => props.theme.secondaryText};
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 0.75rem;
  color: ${(props) => props.theme.primary};
  font-size: 0.95rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const DetailLink = styled(Link)`
  font-size: 0.75rem;
  color: ${(props) => props.theme.primary};
  white-space: nowrap;
`;

/**
 * @param {{ value?: number | string } | null | undefined} edgeDisplay
 * @returns {string | null}
 */
function formatEdgeDisplay(edgeDisplay) {
  if (edgeDisplay?.value == null) return null;
  const pct = (Number(edgeDisplay.value) * 100).toFixed(1);
  const sign = Number(edgeDisplay.value) >= 0 ? '+' : '';
  return `${sign}${pct}%`;
}

/**
 * @param {number} bo
 * @returns {string}
 */
function lineupLabelFromOrder(bo) {
  const n = Number(bo);
  if (n > 0) return `Batting ${n}`;
  if (n === -1) return 'Lineup not posted';
  return 'Not in lineup';
}

const SORT_OPTIONS = [
  { value: 'batting_order', label: 'Batting order' },
  { value: 'player_name', label: 'Player name' },
  { value: 'tb_pred', label: 'TB — prediction' },
  { value: 'tb_odds', label: 'TB — odds' },
  { value: 'tb_edge', label: 'TB — edge' },
  { value: 'hit_pred', label: 'Hit — probability' },
  { value: 'hit_odds', label: 'Hit — odds' },
  { value: 'hit_edge', label: 'Hit — edge' },
  { value: 'two_pred', label: '2+ Hits — probability' },
  { value: 'two_odds', label: '2+ Hits — odds' },
  { value: 'two_edge', label: '2+ Hits — edge' },
  { value: 'hr_pred', label: 'HR — probability' },
  { value: 'hr_odds', label: 'HR — odds' },
  { value: 'hr_edge', label: 'HR — edge' },
];

/**
 * @param {object} props
 * @param {string} props.pickType
 * @param {string} props.playerName
 * @param {string} props.dateStr
 * @returns {React.ReactNode}
 */
function ModelDetailLink({ pickType, playerName, dateStr }) {
  const { theme } = useTheme();
  const to = buildModelPageDeepLink({ pickType, playerName, date: dateStr });
  return (
    <DetailLink theme={theme} to={to}>
      {formatPickTypeLabel(pickType)} →
    </DetailLink>
  );
}

/**
 * Prediction summary text for one model (mobile block).
 *
 * @param {'total_bases' | 'hit' | 'two_hit' | 'hr'} pickType
 * @param {object | null} pick
 * @returns {string}
 */
function predSummaryLine(pickType, pick) {
  if (pick == null) return '—';
  if (pickType === 'total_bases') return formatTotalBasesPredDisplay(pick);
  if (pickType === 'hit') {
    const v = hitPredValue(pick);
    return v != null ? `${(v * 100).toFixed(1)}%` : '—';
  }
  if (pickType === 'two_hit') {
    const v = twoHitPredValue(pick);
    return v != null ? `${(v * 100).toFixed(1)}%` : '—';
  }
  if (pickType === 'hr') {
    const v = hrPredValue(pick);
    return v != null ? `${(v * 100).toFixed(1)}%` : '—';
  }
  return '—';
}

/**
 * @param {object | null} pick
 * @param {string} oddsKey
 * @returns {React.ReactNode}
 */
function OddsOnlyCell({ pick, oddsKey }) {
  const { theme } = useTheme();
  if (pick == null) return <Muted theme={theme}>—</Muted>;
  const odds = pick[oddsKey];
  const display = odds?.display;
  const provider = odds?.provider;
  if (display == null || display === '') return <Muted theme={theme}>—</Muted>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
      {provider ? <SportsbookLogo provider={provider} size="0.95em" /> : null}
      <span>{display}</span>
    </span>
  );
}

function TeamPicksPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamIdRaw = searchParams.get('teamId');
  const teamNameFromUrl = searchParams.get('teamName');
  const teamId = teamIdRaw != null && String(teamIdRaw).trim() !== '' ? Number(teamIdRaw) : NaN;
  const teamIdOk = Number.isFinite(teamId);

  const displayTeamName = resolveMlbTeamDisplayName(teamIdOk ? teamId : null, teamNameFromUrl);
  usePageTitle(
    teamIdOk && displayTeamName
      ? `Team Picks – ${displayTeamName}`
      : 'Team Picks – All models by player',
  );

  const [selectedDate, setSelectedDate] = useState(() => {
    if (typeof window === 'undefined') return new Date();
    return parseModelPageDateFromSearchParams(new URLSearchParams(window.location.search)) ?? new Date();
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  useDateQueryParam(searchParams, setSelectedDate);

  const [sportsbook, setSportsbook] = useState('best');
  useEffect(() => {
    if (user?.preferredSportsbook) setSportsbook(user.preferredSportsbook);
  }, [user?.preferredSportsbook]);

  const [lineupFilter, setLineupFilter] = useState(
    /** @type {'all' | 'in_lineup' | 'bench' | 'not_posted'} */ ('in_lineup'),
  );
  const [filterScope, setFilterScope] = useState(
    /** @type {'any' | 'total_bases' | 'hit' | 'two_hit' | 'hr'} */ ('any'),
  );
  const [minEdgePct, setMinEdgePct] = useState('');
  const [minOddsAmerican, setMinOddsAmerican] = useState('');

  const [sortColumn, setSortColumn] = useState('batting_order');
  const [sortDirection, setSortDirection] = useState(
    /** @type {'asc' | 'desc'} */ ('asc'),
  );

  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  const syncDateToUrl = useCallback(
    (d) => {
      const next = new URLSearchParams(searchParams);
      next.set('date', format(d, 'yyyy-MM-dd'));
      if (teamIdOk) {
        next.set('teamId', String(teamId));
        const label = resolveMlbTeamDisplayName(teamId, teamNameFromUrl);
        if (label) next.set('teamName', label);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, teamIdOk, teamId, teamNameFromUrl],
  );

  /**
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleTeamSelect = (e) => {
    const v = e.target.value;
    const next = new URLSearchParams(searchParams);
    next.set('date', formattedDate);
    if (!v) {
      next.delete('teamId');
      next.delete('teamName');
    } else {
      const id = Number(v);
      const meta = MLB_TEAMS.find((t) => t.id === id);
      next.set('teamId', String(id));
      if (meta) next.set('teamName', meta.name);
      else next.delete('teamName');
    }
    setSearchParams(next, { replace: true });
  };

  const onPickDate = (d) => {
    if (!d) return;
    setSelectedDate(d);
    setIsDatePickerOpen(false);
    syncDateToUrl(d);
  };

  const { data: sportsbooksResp } = useSportsbooks();
  const sportsbooks = sportsbooksResp?.sportsbooks || [];

  const queryOpts = { enabled: teamIdOk };
  const { data: tbResp, isLoading: tbLoading } = useTotalBasesPredictions(
    formattedDate,
    false,
    sportsbook,
    null,
    queryOpts,
  );
  const { data: hitResp, isLoading: hitLoading } = useHitPredictions(formattedDate, false, sportsbook, queryOpts);
  const { data: twoResp, isLoading: twoLoading } = useTwoHitsPredictions(formattedDate, false, sportsbook, queryOpts);
  const { data: hrResp, isLoading: hrLoading } = useHrPredictions(formattedDate, false, sportsbook, queryOpts);

  const loading = teamIdOk && (tbLoading || hitLoading || twoLoading || hrLoading);

  const merged = useMemo(() => {
    if (!teamIdOk) return [];
    const lists = {
      totalBases: filterPredictionsByTeam(tbResp?.predictions, teamId),
      hit: filterPredictionsByTeam(hitResp?.predictions, teamId),
      twoHit: filterPredictionsByTeam(twoResp?.predictions, teamId),
      hr: filterPredictionsByTeam(hrResp?.predictions, teamId),
    };
    return mergePlayerRows(lists);
  }, [teamIdOk, teamId, tbResp, hitResp, twoResp, hrResp]);

  const minEdgeFrac = useMemo(() => {
    const t = minEdgePct.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    return n / 100;
  }, [minEdgePct]);

  const minOddsParsed = useMemo(() => {
    const t = minOddsAmerican.trim();
    if (t === '') return null;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : null;
  }, [minOddsAmerican]);

  const filteredSorted = useMemo(() => {
    let rows = merged.filter((r) => passesLineupFilter(r, lineupFilter));
    if (minEdgeFrac != null) {
      rows = rows.filter((r) => passesMinEdgeFilter(r, minEdgeFrac, filterScope));
    }
    if (minOddsParsed != null) {
      rows = rows.filter((r) => passesMinOddsFilter(r, minOddsParsed, filterScope));
    }
    return sortTeamRows(rows, sortColumn, sortDirection);
  }, [merged, lineupFilter, minEdgeFrac, minOddsParsed, filterScope, sortColumn, sortDirection]);

  const rosterTeamLabel =
    displayTeamName ||
    (teamIdOk ? merged[0]?.teamName || `Team ${teamId}` : '');

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection(col === 'player_name' || col === 'batting_order' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (col) =>
    sortColumn === col ? (
      sortDirection === 'asc' ? (
        <FaSortUp aria-hidden />
      ) : (
        <FaSortDown aria-hidden />
      )
    ) : (
      <FaSort style={{ opacity: 0.45 }} aria-hidden />
    );

  return (
    <Container>
      <PageMeta
        description={
          teamIdOk && rosterTeamLabel
            ? `All model predictions for ${rosterTeamLabel} on ${formattedDate}.`
            : 'Pick an MLB team to compare Total Bases, Hit, 2+ Hits, and Home Run model predictions for every player on the roster.'
        }
      />
      <BackLink theme={theme} to={`/game-picks?date=${encodeURIComponent(formattedDate)}`}>
        ← Game Picks ({formattedDate})
      </BackLink>
      <Header>
        <Title theme={theme}>Team Picks</Title>
        <TeamPickerBar theme={theme}>
          <FilterField theme={theme} style={{ width: '100%' }}>
            Team
            <TeamSelect
              theme={theme}
              aria-label="Select MLB team"
              value={teamIdOk ? String(teamId) : ''}
              onChange={handleTeamSelect}
            >
              <option value="">Select a team…</option>
              {MLB_TEAMS.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </TeamSelect>
          </FilterField>
        </TeamPickerBar>
        <Subtitle theme={theme}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
            {teamIdOk ? (
              <TeamWithLogo teamId={teamId} teamName={rosterTeamLabel || displayTeamName} size={26} />
            ) : null}
            <span>{teamIdOk ? '· ' : ''}{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
          </span>
        </Subtitle>
      </Header>

      <ControlsRow>
        <DateNavBtn type="button" theme={theme} onClick={() => onPickDate(addDays(selectedDate, -1))} aria-label="Previous day">
          <FaChevronLeft aria-hidden />
        </DateNavBtn>
        <DatePicker
          selected={selectedDate}
          onChange={onPickDate}
          dateFormat="yyyy-MM-dd"
          customInput={<DateButtonInput />}
          open={isDatePickerOpen}
          onClickOutside={() => setIsDatePickerOpen(false)}
          onInputClick={() => setIsDatePickerOpen((o) => !o)}
        />
        <DateNavBtn type="button" theme={theme} onClick={() => onPickDate(addDays(selectedDate, 1))} aria-label="Next day">
          <FaChevronRight aria-hidden />
        </DateNavBtn>
        <SportsbookDropdown options={sportsbooks} value={sportsbook} onChange={setSportsbook} />
      </ControlsRow>

      <FilterPanel theme={theme}>
        <FilterField theme={theme}>
          Lineup
          <FilterSelect theme={theme} value={lineupFilter} onChange={(e) => setLineupFilter(/** @type {any} */ (e.target.value))}>
            <option value="all">All players</option>
            <option value="in_lineup">In lineup only</option>
            <option value="bench">Not in lineup</option>
            <option value="not_posted">Lineup not posted</option>
          </FilterSelect>
        </FilterField>
        <FilterField theme={theme}>
          Min edge / odds applies to
          <FilterSelect theme={theme} value={filterScope} onChange={(e) => setFilterScope(/** @type {any} */ (e.target.value))}>
            <option value="any">Any model</option>
            <option value="total_bases">Total Bases</option>
            <option value="hit">Hit</option>
            <option value="two_hit">2+ Hits</option>
            <option value="hr">Home Run</option>
          </FilterSelect>
        </FilterField>
        <FilterField theme={theme}>
          Min edge (%)
          <FilterInput
            theme={theme}
            type="text"
            inputMode="decimal"
            placeholder="e.g. 2"
            value={minEdgePct}
            onChange={(e) => setMinEdgePct(e.target.value)}
            aria-label="Minimum edge percent"
          />
        </FilterField>
        <FilterField theme={theme}>
          Min odds (American)
          <FilterInput
            theme={theme}
            type="text"
            inputMode="text"
            placeholder="e.g. +120"
            value={minOddsAmerican}
            onChange={(e) => setMinOddsAmerican(e.target.value)}
            aria-label="Minimum American odds"
          />
        </FilterField>
        <MobileOnlyFilterField theme={theme}>
          Sort by
          <MobileSortSelect
            theme={theme}
            value={sortColumn}
            onChange={(e) => {
              setSortColumn(e.target.value);
              setSortDirection(e.target.value === 'player_name' || e.target.value === 'batting_order' ? 'asc' : 'desc');
            }}
            aria-label="Sort by column"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </MobileSortSelect>
        </MobileOnlyFilterField>
      </FilterPanel>

      {loading && <LoadingMessage theme={theme}>Loading predictions…</LoadingMessage>}

      {!teamIdOk && !loading && (
        <EmptyMessage theme={theme}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.primaryText }}>Choose a team</p>
          <p style={{ marginTop: '0.5rem' }}>
            Select a club from the dropdown above, or open{' '}
            <Link to={`/game-picks?date=${encodeURIComponent(formattedDate)}`} style={{ color: theme.primary }}>
              Game Picks
            </Link>{' '}
            and click a team name to jump here with that roster pre-selected.
          </p>
        </EmptyMessage>
      )}

      {teamIdOk && !loading && merged.length === 0 && (
        <EmptyMessage theme={theme}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.primaryText }}>No predictions for this team</p>
          <p style={{ marginTop: '0.5rem' }}>There may be no slate data for this date, or the team ID did not match any player rows. Try another date or return to Game Picks.</p>
        </EmptyMessage>
      )}

      {!loading && merged.length > 0 && filteredSorted.length === 0 && (
        <EmptyMessage theme={theme}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.primaryText }}>No rows match your filters</p>
          <p style={{ marginTop: '0.5rem' }}>Try clearing min edge/odds, switching lineup filter to “All players”, or pick another date.</p>
        </EmptyMessage>
      )}

      {!loading && filteredSorted.length > 0 && (
        <>
          <DesktopTableSection theme={theme}>
            <TableWrap>
              <Table theme={theme}>
                <thead>
                  <tr>
                    <ThGroup colSpan={4}>Player</ThGroup>
                    <ThGroup colSpan={4}>{formatPickTypeLabel('total_bases')}</ThGroup>
                    <ThGroup colSpan={4}>{formatPickTypeLabel('hit')}</ThGroup>
                    <ThGroup colSpan={4}>{formatPickTypeLabel('two_hit')}</ThGroup>
                    <ThGroup colSpan={4}>{formatPickTypeLabel('hr')}</ThGroup>
                  </tr>
                  <tr>
                    <Th $sortable theme={theme} onClick={() => handleSort('player_name')}>
                      Player {sortIcon('player_name')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('batting_order')}>
                      BO {sortIcon('batting_order')}
                    </Th>
                    <Th theme={theme}>Lineup</Th>
                    <Th theme={theme}>Status</Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('tb_pred')}>
                      Pred {sortIcon('tb_pred')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('tb_odds')}>
                      Odds {sortIcon('tb_odds')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('tb_edge')}>
                      Edge {sortIcon('tb_edge')}
                    </Th>
                    <Th theme={theme}>TB link</Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hit_pred')}>
                      Prob {sortIcon('hit_pred')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hit_odds')}>
                      Odds {sortIcon('hit_odds')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hit_edge')}>
                      Edge {sortIcon('hit_edge')}
                    </Th>
                    <Th theme={theme}>Hit link</Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('two_pred')}>
                      Prob {sortIcon('two_pred')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('two_odds')}>
                      Odds {sortIcon('two_odds')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('two_edge')}>
                      Edge {sortIcon('two_edge')}
                    </Th>
                    <Th theme={theme}>2+ link</Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hr_pred')}>
                      Prob {sortIcon('hr_pred')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hr_odds')}>
                      Odds {sortIcon('hr_odds')}
                    </Th>
                    <Th $sortable theme={theme} onClick={() => handleSort('hr_edge')}>
                      Edge {sortIcon('hr_edge')}
                    </Th>
                    <Th theme={theme}>HR link</Th>
                  </tr>
                </thead>
                <Tbody theme={theme}>
                  {filteredSorted.map((row) => (
                    <tr key={row.playerId}>
                      <Td>
                        <PlayerWithAvatar playerId={row.playerId} playerName={row.playerName} size={24} />
                      </Td>
                      <Td>{Number(row.battingOrder ?? -1) > 0 ? row.battingOrder : '—'}</Td>
                      <Td>{lineupLabelFromOrder(row.battingOrder)}</Td>
                      <Td>
                        <StatusWithGamedayLink status={row.gameStatus} gameId={row.gameId} />
                      </Td>
                      <Td>{formatTotalBasesPredDisplay(row.totalBases)}</Td>
                      <Td>
                        <OddsOnlyCell pick={row.totalBases} oddsKey="total_bases_odds_display" />
                      </Td>
                      <Td>{formatEdgeDisplay(row.totalBases?.total_bases_edge_display) || '—'}</Td>
                      <Td>
                        <ModelDetailLink pickType="total_bases" playerName={row.playerName} dateStr={formattedDate} />
                      </Td>
                      <Td>{hitPredValue(row.hit) != null ? `${(hitPredValue(row.hit) * 100).toFixed(1)}%` : '—'}</Td>
                      <Td>
                        <OddsOnlyCell pick={row.hit} oddsKey="hit_odds_display" />
                      </Td>
                      <Td>{formatEdgeDisplay(row.hit?.hit_edge_display) || '—'}</Td>
                      <Td>
                        <ModelDetailLink pickType="hit" playerName={row.playerName} dateStr={formattedDate} />
                      </Td>
                      <Td>{twoHitPredValue(row.twoHit) != null ? `${(twoHitPredValue(row.twoHit) * 100).toFixed(1)}%` : '—'}</Td>
                      <Td>
                        <OddsOnlyCell pick={row.twoHit} oddsKey="two_hit_odds_display" />
                      </Td>
                      <Td>{formatEdgeDisplay(row.twoHit?.two_hit_edge_display) || '—'}</Td>
                      <Td>
                        <ModelDetailLink pickType="two_hit" playerName={row.playerName} dateStr={formattedDate} />
                      </Td>
                      <Td>{hrPredValue(row.hr) != null ? `${(hrPredValue(row.hr) * 100).toFixed(1)}%` : '—'}</Td>
                      <Td>
                        <OddsOnlyCell pick={row.hr} oddsKey="hr_odds_display" />
                      </Td>
                      <Td>{formatEdgeDisplay(row.hr?.hr_edge_display) || '—'}</Td>
                      <Td>
                        <ModelDetailLink pickType="hr" playerName={row.playerName} dateStr={formattedDate} />
                      </Td>
                    </tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrap>
          </DesktopTableSection>

          <CardList>
            {filteredSorted.map((row) => (
              <PlayerCard key={row.playerId} theme={theme}>
                <CardPlayerRow>
                  <PlayerWithAvatar playerId={row.playerId} playerName={row.playerName} size={36} showName={false} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: theme.primaryText }}>{row.playerName}</div>
                    <Muted theme={theme} style={{ fontSize: '0.85rem' }}>
                      {lineupLabelFromOrder(row.battingOrder)}
                      {' · '}
                      <StatusWithGamedayLink status={row.gameStatus} gameId={row.gameId} />
                    </Muted>
                    {row.hit && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.8rem' }}>
                        <MatchupCell
                          homeAway={row.hit.homeAway ?? row.hit.home_away}
                          opponentTeamId={row.hit.opponentTeamId ?? row.hit.opponent_team_id}
                          opponent={row.hit.opponent ?? row.hit.opponent_name}
                        />
                      </div>
                    )}
                  </div>
                </CardPlayerRow>
                <ModelBlock theme={theme}>
                  <ModelBlockTitle theme={theme}>{formatPickTypeLabel('total_bases')}</ModelBlockTitle>
                  <ModelGrid theme={theme}>
                    <span>Pred</span>
                    <span>{predSummaryLine('total_bases', row.totalBases)}</span>
                    <span>Odds</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
                      {row.totalBases?.total_bases_odds_display?.provider ? (
                        <SportsbookLogo provider={row.totalBases.total_bases_odds_display.provider} size="0.9em" />
                      ) : null}
                      {row.totalBases?.total_bases_odds_display?.display ?? '—'}
                    </span>
                    <span>Edge</span>
                    <span>{formatEdgeDisplay(row.totalBases?.total_bases_edge_display) || '—'}</span>
                  </ModelGrid>
                  <ModelDetailLink pickType="total_bases" playerName={row.playerName} dateStr={formattedDate} />
                </ModelBlock>
                <ModelBlock theme={theme}>
                  <ModelBlockTitle theme={theme}>{formatPickTypeLabel('hit')}</ModelBlockTitle>
                  <ModelGrid theme={theme}>
                    <span>Prob</span>
                    <span>{predSummaryLine('hit', row.hit)}</span>
                    <span>Odds</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
                      {row.hit?.hit_odds_display?.provider ? <SportsbookLogo provider={row.hit.hit_odds_display.provider} size="0.9em" /> : null}
                      {row.hit?.hit_odds_display?.display ?? '—'}
                    </span>
                    <span>Edge</span>
                    <span>{formatEdgeDisplay(row.hit?.hit_edge_display) || '—'}</span>
                  </ModelGrid>
                  <ModelDetailLink pickType="hit" playerName={row.playerName} dateStr={formattedDate} />
                </ModelBlock>
                <ModelBlock theme={theme}>
                  <ModelBlockTitle theme={theme}>{formatPickTypeLabel('two_hit')}</ModelBlockTitle>
                  <ModelGrid theme={theme}>
                    <span>Prob</span>
                    <span>{predSummaryLine('two_hit', row.twoHit)}</span>
                    <span>Odds</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
                      {row.twoHit?.two_hit_odds_display?.provider ? <SportsbookLogo provider={row.twoHit.two_hit_odds_display.provider} size="0.9em" /> : null}
                      {row.twoHit?.two_hit_odds_display?.display ?? '—'}
                    </span>
                    <span>Edge</span>
                    <span>{formatEdgeDisplay(row.twoHit?.two_hit_edge_display) || '—'}</span>
                  </ModelGrid>
                  <ModelDetailLink pickType="two_hit" playerName={row.playerName} dateStr={formattedDate} />
                </ModelBlock>
                <ModelBlock theme={theme}>
                  <ModelBlockTitle theme={theme}>{formatPickTypeLabel('hr')}</ModelBlockTitle>
                  <ModelGrid theme={theme}>
                    <span>Prob</span>
                    <span>{predSummaryLine('hr', row.hr)}</span>
                    <span>Odds</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
                      {row.hr?.hr_odds_display?.provider ? <SportsbookLogo provider={row.hr.hr_odds_display.provider} size="0.9em" /> : null}
                      {row.hr?.hr_odds_display?.display ?? '—'}
                    </span>
                    <span>Edge</span>
                    <span>{formatEdgeDisplay(row.hr?.hr_edge_display) || '—'}</span>
                  </ModelGrid>
                  <ModelDetailLink pickType="hr" playerName={row.playerName} dateStr={formattedDate} />
                </ModelBlock>
              </PlayerCard>
            ))}
          </CardList>
        </>
      )}
    </Container>
  );
}

export default TeamPicksPage;
